/**
 * Force-directed correlation graph (ported from console ForceGraphDiagram).
 * Styled for the arbitiumdemo Investigation Workspace.
 */

import * as d3 from 'd3';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ElementGraphLayer,
  ForceGraphData,
  ForceGraphLink,
  ForceGraphNode,
  ThreatSeverity,
} from './buildCombinedGraph';
import { PlaceMemory, placeFromMemory, rememberPlaces, restartAlpha, shiftPlaces } from './forcePositions';
import { ARROW_DIRECTION, fitTransform, nextInDirection } from './graphView';
import { ALL_NAMED_IN_VIEW, LABEL_GAP_PX, placeLabels, type LabelCandidate, type PlacedLabel } from './labelPlacement';
import { prefersReducedMotion } from '../../../lib/motion';

// Colours are design tokens (src/ui/tokens.css); SVG attributes resolve var() at paint time.
// The graph is quiet at rest: shape says what a node is (circle, square, diamond), every node and line is slate, and only
// two colours light up: honey for an incident that waits on a person and red for a critical one (colour rules 2 and 5).
// Indigo is for the selection and for a campaign, as everywhere else. The Domain colors switch brings the palette back.
const QUIET = 'var(--t3)';
const NEED_COLOR = 'var(--need)';
const CRIT_COLOR = 'var(--crit)';
const BUSINESS_COLOR = 'var(--layer-business)';

/**
 * Infrastructure domain palette — blues / purples / ambers / cyans.
 * Avoid green (reserved for business) and red (reserved for threats).
 */
const DOMAIN_COLORS = [
  'var(--layer-infra)',
  'var(--acc)',
  'var(--prog)',
  'var(--acc-t)',
  'var(--med)',
  'var(--layer-external)',
  'color-mix(in srgb, var(--acc) 55%, var(--s1))',
  'var(--t3)',
  'var(--sev-h)',
  'color-mix(in srgb, var(--layer-infra) 60%, var(--s1))',
  'var(--acc-f)',
  'color-mix(in srgb, var(--layer-external) 60%, var(--s1))',
  'color-mix(in srgb, var(--acc-t) 60%, var(--s1))',
  'var(--high)',
];

/** Fixed colors for known infrastructure domains (no green / red). */
const DOMAIN_COLOR_BY_NAME: Record<string, string> = {
  database: 'var(--prog)',
  observability: 'var(--acc)',
  identity: 'var(--acc-t)',
  network: 'var(--layer-infra)',
  compute: 'var(--acc-f)',
  storage: 'var(--layer-external)',
  application: 'color-mix(in srgb, var(--acc) 55%, var(--s1))',
};

const THREAT_SEV_RADIUS: Record<ThreatSeverity, number> = {
  critical: 13,
  high: 10.5,
  medium: 8,
  low: 6,
};

const CROSS_LAYER_EDGE = QUIET;
const THREAT_CROSS_EDGE = QUIET;
const CAMPAIGN_MEMBER_EDGE = 'var(--acc)';
const BASE_EDGE = QUIET;
const DANGLING_EDGE = QUIET;

/** How strongly a line is drawn: a plain relation is faint, an incident reaching an asset a little more, a campaign's own lines most. */
function linkOpacity(kind: { campaignMember?: boolean; reach?: boolean; crossLayer?: boolean }): number {
  if (kind.campaignMember) return 0.5;
  if (kind.reach || kind.crossLayer) return 0.6;
  return 0.38;
}

type SimNode = ForceGraphNode & {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  clusterX?: number;
  clusterY?: number;
};

type SimLink = d3.SimulationLinkDatum<SimNode> & {
  label: string;
  dangling?: boolean;
  crossLayer?: boolean;
  /** Invisible layout-only link to keep campaign members close. */
  cohesion?: boolean;
};

/** Below this energy a simulation has all but come to rest: the layout is worth holding fixed on the next rebuild. */
const SETTLED_ALPHA = 0.05;

const COHESION_DISTANCE = 58;
const COHESION_STRENGTH = 0.82;

/** Pairwise invisible links so unlinked campaign members still pack together. */
function buildCampaignCohesionLinks(nodes: SimNode[]): SimLink[] {
  const byCluster = new Map<string, SimNode[]>();
  for (const node of nodes) {
    const key = node.campaignCluster;
    if (!key) continue;
    if (!byCluster.has(key)) byCluster.set(key, []);
    byCluster.get(key)!.push(node);
  }
  const out: SimLink[] = [];
  Array.from(byCluster.values()).forEach((members) => {
    if (members.length < 2) return;
    for (let i = 0; i < members.length; i += 1) {
      for (let j = i + 1; j < members.length; j += 1) {
        out.push({
          source: members[i].id,
          target: members[j].id,
          label: 'CLUSTER_COHESION',
          cohesion: true,
        });
      }
    }
  });
  return out;
}

function resolveLayer(node: Pick<ForceGraphNode, 'layer'>): ElementGraphLayer {
  return node.layer ?? 'infrastructure';
}

function threatSeverity(node: Pick<ForceGraphNode, 'severity'>): ThreatSeverity {
  const value = String(node.severity || '').toLowerCase();
  return value === 'critical' || value === 'high' || value === 'medium' || value === 'low'
    ? value
    : 'medium';
}

function isHandledThreat(node: Pick<ForceGraphNode, 'layer' | 'needsDecision'>): boolean {
  return resolveLayer(node) === 'threat' && !node.needsDecision;
}

function nodeRadius(node: SimNode): number {
  if (node.isDangling) return 4;
  const base = 5 + Math.min(node.linkCount, 6);
  // EBR blast origin: larger hex so the start reads above the tree.
  if (node.isBlastOrigin) return Math.max(base * 1.85, 14);
  // Threat diamonds scale by severity (see Threat node key).
  if (resolveLayer(node) === 'threat') return THREAT_SEV_RADIUS[threatSeverity(node)];
  return base;
}

/** Pointy-top hexagon for EBR blast origin. */
function hexagonPath(r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / 6;
    pts.push(`${i === 0 ? 'M' : 'L'} ${r * Math.cos(a)},${r * Math.sin(a)}`);
  }
  return `${pts.join(' ')} Z`;
}

function layerShapePath(layer: ElementGraphLayer, r: number): string {
  if (layer === 'business') {
    const s = r * 0.9;
    return `M ${-s},${-s} L ${s},${-s} L ${s},${s} L ${-s},${s} Z`;
  }
  if (layer === 'threat') {
    return `M 0,${-r} L ${r},0 L 0,${r} L ${-r},0 Z`;
  }
  const steps = 16;
  const pts: string[] = [];
  for (let i = 0; i < steps; i++) {
    const a = (2 * Math.PI * i) / steps;
    pts.push(`${i === 0 ? 'M' : 'L'} ${r * Math.cos(a)},${r * Math.sin(a)}`);
  }
  return `${pts.join(' ')} Z`;
}

function nodeShapePath(node: SimNode, r: number): string {
  if (node.isBlastOrigin) return hexagonPath(r);
  return layerShapePath(resolveLayer(node), r);
}

function createDomainColorScale(domains: string[]) {
  const unique = Array.from(new Set(domains.map((d) => d.toLowerCase()))).sort();
  const unmapped = unique.filter((domain) => !DOMAIN_COLOR_BY_NAME[domain]);
  const scale = d3.scaleOrdinal<string, string>().domain(unmapped).range(DOMAIN_COLORS);
  return (domain: string) => {
    const key = domain.toLowerCase();
    return DOMAIN_COLOR_BY_NAME[key] ?? scale(key) ?? DOMAIN_COLORS[0];
  };
}

export { createDomainColorScale };

function nodeFillColor(
  node: Pick<ForceGraphNode, 'layer' | 'group' | 'needsDecision'>,
  colorByDomain: (domain: string) => string,
  domainColorsEnabled = true
): string {
  const layer = resolveLayer(node);
  if (layer === 'threat') return isHandledThreat(node) ? 'transparent' : NEED_COLOR;
  if (!domainColorsEnabled) return QUIET;
  if (layer === 'business') return BUSINESS_COLOR;
  return colorByDomain(node.group);
}

/** The outline of a node: red for a critical incident, slate for a handled one, a gap of the canvas colour for the rest. */
function nodeStrokeColor(node: Pick<ForceGraphNode, 'layer' | 'needsDecision' | 'severity'>): string {
  if (resolveLayer(node) !== 'threat') return 'var(--s1)';
  if (threatSeverity(node) === 'critical') return CRIT_COLOR;
  return isHandledThreat(node) ? QUIET : 'var(--s1)';
}

function nodeStrokeWidth(node: Pick<ForceGraphNode, 'layer' | 'needsDecision' | 'severity' | 'isBlastOrigin'>): number {
  if (node.isBlastOrigin) return 1.6;
  if (resolveLayer(node) === 'threat' && (isHandledThreat(node) || threatSeverity(node) === 'critical')) return 1.6;
  return 1.2;
}

type LegendTone = 'quiet' | 'crit' | 'need';
const LEGEND_TONE: Record<LegendTone, string> = { quiet: QUIET, crit: CRIT_COLOR, need: NEED_COLOR };

function LegendDiamond({
  size,
  tone = 'quiet',
  filled = false,
  pulse = false,
}: {
  size: number;
  tone?: LegendTone;
  filled?: boolean;
  pulse?: boolean;
}) {
  return (
    <span
      className={`c3-legend-diamond${pulse ? ' is-pulse' : ''}${filled ? '' : ' is-hollow'}`}
      style={{
        width: size,
        height: size,
        backgroundColor: filled ? LEGEND_TONE[tone] : 'transparent',
        borderColor: LEGEND_TONE[tone],
      }}
      aria-hidden
    />
  );
}

export type EntityGraphLegendProps = {
  stats?: { nodeCount: number; linkCount: number };
  openIncidents?: number;
  /** More keys, drawn between the lines and the totals (the Workspace puts the types of entity here). */
  children?: React.ReactNode;
};

/** Compact panel: what an incident's size, colour and outline say, and the two kinds of line. */
export function EntityGraphLegend({ stats, openIncidents, children }: EntityGraphLegendProps) {
  return (
    <div className="c3-entity-legend">
      <div className="c3-entity-legend-k">Incidents</div>
      <div className="c3-entity-legend-sev">
        <div className="c3-entity-legend-sev-diamonds">
          <span className="c3-entity-legend-sev-item">
            <span className="c3-legend-cluster" aria-hidden />
            <span>campaign</span>
          </span>
          {([
            ['critical', 11, 'crit'],
            ['high', 9, 'quiet'],
            ['medium', 7, 'quiet'],
            ['low', 5, 'quiet'],
          ] as const).map(([label, size, tone]) => (
            <span key={label} className="c3-entity-legend-sev-item">
              <LegendDiamond size={size} tone={tone} />
              <span>{label}</span>
            </span>
          ))}
        </div>
        <div className="c3-entity-legend-status">
          <span className="c3-entity-legend-status-item">
            <LegendDiamond size={8} tone="need" filled pulse />
            <span>needs a decision</span>
          </span>
          <span className="c3-entity-legend-status-item">
            <LegendDiamond size={8} />
            <span>handled</span>
          </span>
        </div>
      </div>

      <div className="c3-entity-legend-k">Lines</div>
      <div className="c3-entity-legend-status">
        <span className="c3-entity-legend-status-item">
          <span className="c3-legend-edge" aria-hidden />
          <span>relates to</span>
        </span>
        <span className="c3-entity-legend-status-item">
          <span className="c3-legend-edge is-dashed" aria-hidden />
          <span>affects</span>
        </span>
      </div>

      {children}

      {stats ? (
        <div className="c3-entity-legend-foot">
          <div>
            graph · {stats.nodeCount.toLocaleString('en-US')} nodes · {stats.linkCount.toLocaleString('en-US')}{' '}
            edges
          </div>
          {openIncidents != null ? (
            <div>{openIncidents.toLocaleString('en-US')} open incidents</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** @deprecated Prefer EntityGraphLegend — kept for existing imports. */
export function LayerShapeLegend() {
  return <EntityGraphLegend />;
}

/** Domain color legend — click chips to focus domains (others attenuate). */
export function DomainLegend({
  domains,
  colorDomains,
  enabledDomains,
  onToggleDomain,
  domainColorsEnabled = true,
}: {
  domains: string[];
  /** Full set used by the graph color scale (keeps swatches in sync). */
  colorDomains?: string[];
  /** When set, domains not in the set render as inactive chips. */
  enabledDomains?: Set<string>;
  onToggleDomain?: (domain: string) => void;
  /** When false, every swatch is the quiet slate of the graph at rest. */
  domainColorsEnabled?: boolean;
}) {
  const colorByDomain = useMemo(
    () => createDomainColorScale(colorDomains && colorDomains.length > 0 ? colorDomains : domains),
    [domains, colorDomains]
  );

  if (domains.length === 0) return null;

  const filtering =
    enabledDomains != null &&
    enabledDomains.size > 0 &&
    enabledDomains.size < domains.length;

  return (
    <div className="flex flex-wrap gap-2">
      {domains.map((domain) => {
        const active = !filtering || enabledDomains!.has(domain);
        const interactive = Boolean(onToggleDomain);
        const className = [
          'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] transition-opacity',
          active
            ? 'border-c3-purple/60 bg-c3-tile text-c3-white'
            : 'border-c3-tile-bd bg-c3-tile/40 text-c3-mute opacity-45',
          interactive ? 'cursor-pointer hover:border-c3-purple' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const swatchColor = domainColorsEnabled ? colorByDomain(domain) : QUIET;
        const content = (
          <>
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-c3-mute/40"
              style={{ backgroundColor: swatchColor }}
            />
            <span className="capitalize">{domain.replace(/_/g, ' ')}</span>
          </>
        );
        if (!interactive) {
          return (
            <div key={domain} className={className}>
              {content}
            </div>
          );
        }
        return (
          <button
            key={domain}
            type="button"
            className={className}
            aria-pressed={active}
            title={active ? `Dim ${domain}` : `Focus ${domain}`}
            onClick={() => onToggleDomain?.(domain)}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

/** Invisible perimeter hit width for campaign hulls (option B). */
const CAMPAIGN_HULL_HIT_STROKE = 14;

interface ForceGraphDiagramProps {
  graphData: ForceGraphData;
  selectedNodeId: string | null;
  onSelectNode: (node: ForceGraphNode | null) => void;
  /** Click on campaign cluster perimeter → open that campaign (id = threat_elements._id). */
  onSelectCampaignCluster?: (campaignId: string) => void;
  /** Scoped campaign id — highlights that hull vs other clusters. */
  activeCampaignId?: string | null;
  className?: string;
  /** Infrastructure domains to attenuate (others stay full opacity). Empty = no filter. */
  attenuatedInfraDomains?: Set<string>;
  /** Node ids to attenuate (campaign siblings / out-of-incident neighbors). */
  attenuatedNodeIds?: Set<string>;
  /** When false (the graph at rest), every node is slate; the palette of the domains is the switch's. */
  domainColorsEnabled?: boolean;
  /** Changing it forgets where the nodes were, so the graph is laid out afresh (the Refresh button, a new organization). */
  layoutKey?: number;
}

function paintCampaignHull(
  path: d3.Selection<SVGPathElement, { key: string; members: SimNode[] }, any, any>,
  activeCampaignId?: string | null
) {
  path
    .classed('is-active', (d) => Boolean(activeCampaignId && d.key === activeCampaignId))
    .attr('fill', (d) =>
      activeCampaignId && d.key === activeCampaignId
        ? 'color-mix(in srgb, var(--acc) 20%, transparent)'
        : 'color-mix(in srgb, var(--acc) 6%, transparent)'
    )
    .attr('stroke', (d) =>
      activeCampaignId && d.key === activeCampaignId
        ? 'var(--acc)'
        : 'color-mix(in srgb, var(--acc) 45%, transparent)'
    )
    .attr('stroke-dasharray', (d) =>
      activeCampaignId && d.key === activeCampaignId ? null : '5 4'
    )
    .attr('stroke-width', (d) => (activeCampaignId && d.key === activeCampaignId ? 2.75 : 1));
}

const DIM_NODE_OPACITY = 0.16;
const DIM_LINK_OPACITY = 0.12;

function isInfraDomainAttenuated(
  node: Pick<ForceGraphNode, 'layer' | 'group'>,
  attenuated?: Set<string>
): boolean {
  if (!attenuated || attenuated.size === 0) return false;
  if (resolveLayer(node) !== 'infrastructure') return false;
  return attenuated.has(node.group);
}

function isNodeAttenuated(
  node: Pick<ForceGraphNode, 'id' | 'layer' | 'group'>,
  domains?: Set<string>,
  ids?: Set<string>
): boolean {
  if (ids && ids.size > 0 && ids.has(node.id)) return true;
  return isInfraDomainAttenuated(node, domains);
}

function linkEndpointId(end: SimNode | string | number | undefined): string | null {
  if (end == null) return null;
  if (typeof end === 'object') return end.id;
  return String(end);
}

function endpointNode(
  end: SimNode | string | number | undefined,
  nodeById: Map<string, SimNode>
): SimNode | undefined {
  if (end != null && typeof end === 'object') return end;
  const id = linkEndpointId(end);
  return id ? nodeById.get(id) : undefined;
}

function isCampaignNode(node: Pick<ForceGraphNode, 'layer' | 'universalType'>): boolean {
  return resolveLayer(node) === 'threat' && String(node.universalType || '').toLowerCase() === 'campaign';
}

function isActionNode(node: Pick<ForceGraphNode, 'layer' | 'universalType'>): boolean {
  return resolveLayer(node) === 'threat' && String(node.universalType || '').toLowerCase() === 'action';
}

function isAssetLayer(node: Pick<ForceGraphNode, 'layer'>): boolean {
  const layer = resolveLayer(node);
  return layer === 'infrastructure' || layer === 'business';
}

function threatCrossEnds(
  link: SimLink,
  nodeById: Map<string, SimNode>
): { from: SimNode; to: SimNode } | null {
  const source = endpointNode(link.source, nodeById);
  const target = endpointNode(link.target, nodeById);
  if (!source || !target) return null;
  if (isActionNode(source) && isAssetLayer(target)) return { from: source, to: target };
  if (isActionNode(target) && isAssetLayer(source)) return { from: target, to: source };
  return null;
}

function isCampaignMemberLink(link: SimLink, nodeById: Map<string, SimNode>): boolean {
  const source = endpointNode(link.source, nodeById);
  const target = endpointNode(link.target, nodeById);
  if (!source || !target) return false;
  return (
    (isCampaignNode(source) && isActionNode(target)) ||
    (isCampaignNode(target) && isActionNode(source))
  );
}

function isThreatIntra(link: SimLink, nodeById: Map<string, SimNode>): boolean {
  const source = endpointNode(link.source, nodeById);
  const target = endpointNode(link.target, nodeById);
  if (!source || !target) return false;
  if (isCampaignMemberLink(link, nodeById)) return false;
  return resolveLayer(source) === 'threat' && resolveLayer(target) === 'threat';
}

function hullPathFor(members: SimNode[]): string | null {
  const pts: Array<[number, number]> = [];
  for (const node of members) {
    const r = nodeRadius(node) + 16;
    const x = node.x ?? 0;
    const y = node.y ?? 0;
    for (let i = 0; i < 8; i += 1) {
      const angle = (Math.PI * 2 * i) / 8;
      pts.push([x + Math.cos(angle) * r, y + Math.sin(angle) * r]);
    }
  }
  const hull = d3.polygonHull(pts);
  if (!hull || hull.length < 3) return null;
  const line = d3
    .line<[number, number]>()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(d3.curveCatmullRomClosed.alpha(0.8));
  return line(hull);
}

function shortenEnds(
  from: SimNode,
  to: SimNode,
  extraTail: number
): { x1: number; y1: number; x2: number; y2: number } {
  const x1 = from.x ?? 0;
  const y1 = from.y ?? 0;
  const x2 = to.x ?? 0;
  const y2 = to.y ?? 0;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const start = nodeRadius(from);
  const end = nodeRadius(to) + extraTail;
  return {
    x1: x1 + (dx / len) * start,
    y1: y1 + (dy / len) * start,
    x2: x2 - (dx / len) * end,
    y2: y2 - (dy / len) * end,
  };
}

const ForceGraphDiagram: React.FC<ForceGraphDiagramProps> = ({
  graphData,
  selectedNodeId,
  onSelectNode,
  onSelectCampaignCluster,
  activeCampaignId = null,
  className = '',
  attenuatedInfraDomains,
  attenuatedNodeIds,
  domainColorsEnabled = true,
  layoutKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const onSelectNodeRef = useRef(onSelectNode);
  const onSelectCampaignClusterRef = useRef(onSelectCampaignCluster);
  const activeCampaignIdRef = useRef(activeCampaignId);
  const domainColorsEnabledRef = useRef(domainColorsEnabled);
  const [dimensions, setDimensions] = useState({ width: 800, height: 360 });
  // Where each node was, by id, across rebuilds of the simulation: a new scope, the scrubber, the clock or a resize
  // redraws the graph, and the nodes that stay must stay where they were.
  const placeMemory = useRef<PlaceMemory>(new Map());
  const lastSize = useRef(dimensions);
  const lastLayoutKey = useRef(layoutKey);
  const lastNodeCount = useRef(0);
  // The graph is fitted in its canvas when it is first drawn, after a Refresh, and when a big graph narrows to a few nodes,
  // unless the person has zoomed or panned since; a redraw that keeps the nodes in place never moves the view.
  const fitPending = useRef(true);
  const userZoomed = useRef(false);
  const selectedNodeIdRef = useRef(selectedNodeId);
  // Chooses again which nodes are named; the drawing sets it, a change of selection calls it.
  const placeLabelsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    domainColorsEnabledRef.current = domainColorsEnabled;
  }, [domainColorsEnabled]);

  useEffect(() => {
    onSelectNodeRef.current = onSelectNode;
  }, [onSelectNode]);

  useEffect(() => {
    onSelectCampaignClusterRef.current = onSelectCampaignCluster;
  }, [onSelectCampaignCluster]);

  useEffect(() => {
    activeCampaignIdRef.current = activeCampaignId;
  }, [activeCampaignId]);

  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setDimensions({
        width: Math.max(320, Math.floor(width)),
        height: Math.max(280, Math.floor(height)),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const colorByDomain = useMemo(() => {
    const domains = Array.from(new Set(graphData.nodes.map((n) => n.group)));
    return createDomainColorScale(domains);
  }, [graphData.nodes]);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement || graphData.nodes.length === 0) return;

    const memory = placeMemory.current;
    const { width, height } = dimensions;
    if (lastLayoutKey.current !== layoutKey) {
      memory.clear();
      lastLayoutKey.current = layoutKey;
      fitPending.current = true;
      userZoomed.current = false;
    }
    const nodes: SimNode[] = graphData.nodes.map((node) => ({ ...node }));
    const links: SimLink[] = graphData.links.map((link) => ({ ...link }));
    const cohesionLinks = buildCampaignCohesionLinks(nodes);
    const forceLinks: SimLink[] = cohesionLinks.length ? [...links, ...cohesionLinks] : links;
    const nodeById = new Map(nodes.map((node) => [node.id, node]));

    const clusterKey = (node: SimNode) => node.campaignCluster || node.group;
    const groups = Array.from(new Set(nodes.map(clusterKey))).sort();
    const groupIndex = new Map(groups.map((group, index) => [group, index]));
    const clusterRadius = Math.min(width, height) * 0.32;
    for (const node of nodes) {
      const index = groupIndex.get(clusterKey(node)) ?? 0;
      const angle = (2 * Math.PI * index) / Math.max(groups.length, 1);
      node.clusterX = width / 2 + clusterRadius * Math.cos(angle);
      node.clusterY = height / 2 + clusterRadius * Math.sin(angle);
      // Seed campaign members near each other so cohesion settles faster.
      if (node.campaignCluster) {
        const jitter = (index * 13 + node.id.length * 7) % 40;
        node.x = (node.clusterX ?? width / 2) + (jitter - 20);
        node.y = (node.clusterY ?? height / 2) + ((jitter * 3) % 36) - 18;
      }
    }

    // A resize moves the middle of the canvas by half of what it grew: the graph follows as one piece.
    shiftPlaces(
      memory,
      (width - lastSize.current.width) / 2,
      (height - lastSize.current.height) / 2
    );
    lastSize.current = dimensions;

    const { kept, added, unsettled, held, reflow } = placeFromMemory(
      nodes,
      links,
      memory,
      { x: width / 2, y: height / 2 },
      lastNodeCount.current
    );
    lastNodeCount.current = nodes.length;
    if (reflow) {
      fitPending.current = true;
      userZoomed.current = false;
    } else if (added >= kept) {
      // The graph more than doubled (the whole graph again after one incident): fit it, unless the person set a zoom.
      fitPending.current = true;
    }

    // A redraw destroys the node under the keyboard focus: note it, to give the focus back to the same node.
    const active = document.activeElement as (Element & { __data__?: SimNode }) | null;
    const focusedId = active && svgElement.contains(active) ? active.__data__?.id : undefined;

    const svg = d3.select(svgElement);
    svg.selectAll('*').remove();
    svg.append('defs').append('marker')
      .attr('id', 'c3-threat-arrow')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 5)
      .attr('markerWidth', 7)
      .attr('markerHeight', 7)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M 0 0 L 10 5 L 0 10 z')
      .attr('fill', THREAT_CROSS_EDGE);

    // The zoom the person set survives the rebuild: d3 keeps it on the element, the new layer only has to wear it.
    const zoomLayer = svg.append('g').attr('transform', d3.zoomTransform(svgElement).toString());
    // The names are drawn in screen pixels, over the zoomed layer, so they stay 12 px at any zoom.
    const labelLayer = svg
      .append('g')
      .attr('class', 'c3-sim-labels')
      .attr('aria-hidden', 'true')
      .attr('pointer-events', 'none');
    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        zoomLayer.attr('transform', event.transform.toString());
        positionLabels();
        // A zoom with a source event is the person's (wheel, drag, double click); the fit has none.
        if (event.sourceEvent) userZoomed.current = true;
      })
      .on('end', () => placeLabelSet());
    svg.call(zoomBehavior);

    const fitIfPending = () => {
      if (!fitPending.current || userZoomed.current) return;
      fitPending.current = false;
      const fit = fitTransform(
        nodes.filter((n) => n.x != null && n.y != null).map((n) => ({ x: n.x as number, y: n.y as number })),
        { width, height }
      );
      if (!fit) return;
      const target = d3.zoomIdentity.translate(fit.x, fit.y).scale(fit.k);
      if (prefersReducedMotion()) svg.call(zoomBehavior.transform, target);
      else svg.transition().duration(400).call(zoomBehavior.transform as never, target as never);
    };

    const styleLinks = (selection: d3.Selection<SVGLineElement, SimLink, SVGGElement, unknown>) => {
      selection
        .attr('class', (link) => {
          if (threatCrossEnds(link, nodeById)) return 'c3-sim-link is-threat-cross';
          if (isCampaignMemberLink(link, nodeById)) return 'c3-sim-link is-campaign-member';
          return 'c3-sim-link';
        })
        .attr('stroke-width', (link) => {
          if (isCampaignMemberLink(link, nodeById)) return 1.4;
          if (threatCrossEnds(link, nodeById)) return 1.4;
          return link.crossLayer ? 1.4 : link.dangling ? 1 : 1.2;
        })
        .attr('stroke-dasharray', (link) => {
          if (isCampaignMemberLink(link, nodeById)) return null;
          if (threatCrossEnds(link, nodeById)) return '4 3';
          if (isThreatIntra(link, nodeById)) return null;
          return link.crossLayer ? '6 3' : link.dangling ? '4 3' : null;
        })
        .attr('stroke', (link) => {
          if (isCampaignMemberLink(link, nodeById)) return CAMPAIGN_MEMBER_EDGE;
          if (threatCrossEnds(link, nodeById)) return THREAT_CROSS_EDGE;
          if (isThreatIntra(link, nodeById)) return BASE_EDGE;
          return link.crossLayer ? CROSS_LAYER_EDGE : link.dangling ? DANGLING_EDGE : BASE_EDGE;
        })
        .attr('stroke-opacity', (link) =>
          linkOpacity({
            campaignMember: isCampaignMemberLink(link, nodeById),
            reach: Boolean(threatCrossEnds(link, nodeById)),
            crossLayer: link.crossLayer,
          })
        )
        .attr('marker-end', (link) => (threatCrossEnds(link, nodeById) ? 'url(#c3-threat-arrow)' : null));
    };

    const hullGroups = () => {
      const map = new Map<string, SimNode[]>();
      for (const node of nodes) {
        if (!node.campaignCluster) continue;
        if (!map.has(node.campaignCluster)) map.set(node.campaignCluster, []);
        map.get(node.campaignCluster)!.push(node);
      }
      return Array.from(map.entries())
        .filter(([, members]) => members.length >= 2)
        .map(([key, members]) => ({ key, members }));
    };

    type HullDatum = { key: string; members: SimNode[] };

    // Under links/nodes: visible fill is non-interactive; fat invisible stroke is the hit target.
    const hullJoin = zoomLayer
      .append('g')
      .attr('class', 'c3-campaign-hulls')
      .selectAll<SVGGElement, HullDatum>('g.c3-campaign-hull-group')
      .data(hullGroups(), (d) => d.key)
      .join((enter) => {
        const g = enter.append('g').attr('class', 'c3-campaign-hull-group');
        g.append('path')
          .attr('class', 'c3-campaign-hull')
          .attr('pointer-events', 'none');
        g.append('path')
          .attr('class', 'c3-campaign-hull-hit')
          .attr('fill', 'none')
          .attr('stroke', 'transparent')
          .attr('stroke-width', CAMPAIGN_HULL_HIT_STROKE)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('pointer-events', 'stroke')
          .style('cursor', 'pointer');
        return g;
      });

    paintCampaignHull(
      hullJoin.select<SVGPathElement>('path.c3-campaign-hull'),
      activeCampaignIdRef.current
    );

    hullJoin.select<SVGPathElement>('path.c3-campaign-hull-hit').on('click', (event, d) => {
      event.stopPropagation();
      onSelectCampaignClusterRef.current?.(d.key);
    });

    const linkSel = zoomLayer
      .append('g')
      .attr('data-links', 'all')
      .selectAll('line')
      .data(links)
      .join('line');
    styleLinks(linkSel as d3.Selection<SVGLineElement, SimLink, SVGGElement, unknown>);

    const simulation = d3
      .forceSimulation(nodes)
      .force(
        'link',
        d3
          .forceLink<SimNode, SimLink>(forceLinks)
          .id((n) => n.id)
          .distance((link) => {
            if (link.cohesion) return COHESION_DISTANCE;
            if (isCampaignMemberLink(link, nodeById)) return 78;
            if (threatCrossEnds(link, nodeById)) return 118;
            if (isThreatIntra(link, nodeById)) return 72;
            return link.crossLayer ? 90 : 64;
          })
          .strength((link) => (link.cohesion ? COHESION_STRENGTH : 0.48))
      )
      .force('charge', d3.forceManyBody<SimNode>().strength(-170))
      // Nodes held where they were are not pulled back to the middle at full strength: that is what slid the graph.
      .force('center', d3.forceCenter(width / 2, height / 2).strength(held.size > 0 ? 0.04 : 1))
      .force(
        'collide',
        d3.forceCollide<SimNode>().radius((n) => nodeRadius(n) + 10)
      )
      .force(
        'clusterX',
        d3
          .forceX<SimNode>((n) => n.clusterX ?? width / 2)
          .strength((n) => (n.campaignCluster ? 0.14 : 0.05))
      )
      .force(
        'clusterY',
        d3
          .forceY<SimNode>((n) => n.clusterY ?? height / 2)
          .strength((n) => (n.campaignCluster ? 0.14 : 0.05))
      );
    // Full energy only for a first drawing; an update trickles, in proportion to what is new. The nodes that stay were
    // held where they were (see placeFromMemory), so only the new ones move; they are let go when the simulation is quiet.
    simulation.alpha(restartAlpha(kept, added, unsettled));
    let settled = false;
    simulation.on('end', () => {
      held.forEach((id) => {
        const node = nodeById.get(id);
        if (node) {
          node.fx = null;
          node.fy = null;
        }
      });
      held.clear();
      settled = true;
      rememberPlaces(memory, nodes, true);
      fitIfPending();
      placeLabelSet();
    });

    // Which nodes wear a name: the one selected, the one under the pointer or the focus, its neighbours, the incidents that
    // wait on a person or are critical, a campaign, and the entities with the most lines; all of them when there are few
    // in view (a scoped graph, or zoomed in).
    // A name never lands on another name or on a node (labelPlacement.ts).
    let peekId: string | null = null;
    const screenPlace = (node: SimNode) => {
      const t = d3.zoomTransform(svgElement);
      return { x: t.applyX(node.x ?? 0), y: t.applyY(node.y ?? 0), r: nodeRadius(node) * t.k };
    };
    const positionLabels = () => {
      const t = d3.zoomTransform(svgElement);
      labelLayer
        .selectAll<SVGTextElement, PlacedLabel>('text.c3-sim-label')
        .attr('x', (d) => {
          const node = nodeById.get(d.id);
          if (!node) return 0;
          const gap = nodeRadius(node) * t.k + LABEL_GAP_PX;
          return t.applyX(node.x ?? 0) + (d.anchor === 'start' ? gap : -gap);
        })
        .attr('y', (d) => t.applyY(nodeById.get(d.id)?.y ?? 0));
    };
    const placeLabelSet = () => {
      const t = d3.zoomTransform(svgElement);
      const selectedId = selectedNodeIdRef.current;
      const near = new Set<string>();
      if (selectedId) {
        for (const link of links) {
          const a = linkEndpointId(link.source as SimNode | string);
          const b = linkEndpointId(link.target as SimNode | string);
          if (a === selectedId && b) near.add(b);
          if (b === selectedId && a) near.add(a);
        }
      }
      const busiest = new Set(
        nodes
          .filter((n) => resolveLayer(n) !== 'threat')
          .sort((a, b) => b.linkCount - a.linkCount)
          .slice(0, 6)
          .map((n) => n.id)
      );
      const inView = nodes.filter((node) => {
        const at = screenPlace(node);
        return at.x >= 0 && at.x <= width && at.y >= 0 && at.y <= height;
      }).length;
      const nameAll = t.k >= 1.6 || inView <= ALL_NAMED_IN_VIEW;
      const candidates: LabelCandidate[] = [];
      for (const node of nodes) {
        if (node.x == null || node.y == null) continue;
        let priority = -1;
        if (node.id === selectedId || node.id === peekId) priority = 0;
        else if (near.has(node.id)) priority = 1;
        else if (
          resolveLayer(node) === 'threat' &&
          (node.needsDecision || threatSeverity(node) === 'critical' || isCampaignNode(node))
        )
          priority = 2;
        else if (busiest.has(node.id)) priority = 3;
        else if (nameAll) priority = 4;
        if (priority < 0) continue;
        candidates.push({ id: node.id, ...screenPlace(node), text: node.label, priority });
      }
      // The node card is drawn over the canvas by the page: a name under it would be half hidden.
      const card = svgElement.parentElement?.parentElement?.querySelector('.c3-node-card');
      const frame = svgElement.getBoundingClientRect();
      const cardBox = card?.getBoundingClientRect();
      const placed = placeLabels(
        candidates,
        { width, height },
        nodes.map((node) => ({ id: node.id, ...screenPlace(node) })),
        cardBox
          ? [
              {
                left: cardBox.left - frame.left,
                top: cardBox.top - frame.top,
                right: cardBox.right - frame.left,
                bottom: cardBox.bottom - frame.top,
              },
            ]
          : []
      );
      labelLayer
        .selectAll<SVGTextElement, PlacedLabel>('text.c3-sim-label')
        .data(placed, (d) => d.id)
        .join('text')
        .attr('class', (d) => `c3-sim-label${d.priority === 0 ? ' is-asked' : ''}`)
        .attr('text-anchor', (d) => d.anchor)
        .attr('dy', '0.32em')
        .text((d) => d.text);
      positionLabels();
    };
    placeLabelsRef.current = placeLabelSet;

    const nodeGroup = zoomLayer
      .append('g')
      .selectAll<SVGGElement, SimNode>('g.c3-sim-node')
      .data(nodes)
      .join('g')
      .attr('class', 'c3-sim-node')
      .attr('data-node-id', (n) => n.id)
      .attr('opacity', (n) => (n.isDangling ? 0.65 : 0.95))
      .style('cursor', 'pointer');

    // Reach by keyboard: one node is in the Tab order (the selected one, else the first), the arrow keys move between
    // nodes, Enter and Space select as a click does.
    const tabbableId =
      selectedNodeIdRef.current && nodeById.has(selectedNodeIdRef.current) ? selectedNodeIdRef.current : nodes[0]?.id;
    nodeGroup
      .attr('role', 'button')
      .attr('tabindex', (n) => (n.id === tabbableId ? 0 : -1))
      .attr('aria-label', (n) => `${n.label}, ${resolveLayer(n)}`);
    const focusNode = (id: string) => {
      nodeGroup.attr('tabindex', (n) => (n.id === id ? 0 : -1));
      (nodeGroup.filter((n) => n.id === id).node() as SVGGElement | null)?.focus({ preventScroll: true });
    };
    nodeGroup.on('keydown', (event: KeyboardEvent, node) => {
      const direction = ARROW_DIRECTION[event.key];
      if (direction) {
        event.preventDefault();
        const next = nextInDirection(
          nodes.map((n) => ({ id: n.id, x: n.x ?? 0, y: n.y ?? 0 })),
          node.id,
          direction
        );
        if (next) focusNode(next);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelectNodeRef.current(node);
      }
    });
    if (focusedId && nodeById.has(focusedId)) focusNode(focusedId);

    nodeGroup
      .append('path')
      .attr('class', (n) =>
        n.needsDecision && resolveLayer(n) === 'threat' ? 'c3-sim-shape c3-node-pulse' : 'c3-sim-shape'
      )
      .attr('d', (n) => nodeShapePath(n, nodeRadius(n)))
      .attr('fill', (n) => nodeFillColor(n, colorByDomain, domainColorsEnabledRef.current))
      .attr('stroke', (n) => nodeStrokeColor(n))
      .attr('stroke-width', (n) => nodeStrokeWidth(n));

    nodeGroup
      .filter((n) => Boolean(n.attackStep && n.attackStep > 0))
      .append('g')
      .attr('class', 'c3-attack-idx')
      .attr('transform', (n) => {
        const r = nodeRadius(n);
        return `translate(${r * 0.8},${-r * 0.8})`;
      })
      .each(function (n) {
        const badge = d3.select(this);
        badge.append('circle').attr('r', 7).attr('fill', 'var(--acc-f)').attr('stroke', 'var(--s1)').attr('stroke-width', 1);
        badge
          .append('text')
          .attr('class', 'c3-attack-idx-label')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .text(String(n.attackStep));
      });

    // Only a drag that moves the node warms the simulation and pins the node. A plain click is a press and a release
    // on the same spot: it must not disturb the layout, nor leave the clicked node fixed for good.
    let dragMoved = false;
    nodeGroup.call(
      d3
        .drag<SVGGElement, SimNode>()
        .clickDistance(4)
        .on('start', () => {
          dragMoved = false;
        })
        .on('drag', (event, node) => {
          if (!dragMoved) {
            dragMoved = true;
            held.delete(node.id);
            // d3 counts this gesture in event.active during a drag (it is 0 only on start and end), so no test of it here.
            simulation.alphaTarget(0.25).restart();
            node.fx = node.fx ?? node.x;
            node.fy = node.fy ?? node.y;
          }
          node.fx = event.x;
          node.fy = event.y;
        })
        .on('end', (event, node) => {
          if (!dragMoved) return;
          if (!event.active) simulation.alphaTarget(0);
          node.fx = event.x;
          node.fy = event.y;
        })
    );

    const peek = (id: string | null) => {
      peekId = id;
      placeLabelSet();
    };
    nodeGroup
      .on('mouseenter focus', (_event, node) => peek(node.id))
      .on('mouseleave blur', () => peek(null))
      .on('click', (event, node) => {
        event.stopPropagation();
        onSelectNodeRef.current(node);
      })
      .on('dblclick', (event, node) => {
        event.stopPropagation();
        node.fx = null;
        node.fy = null;
        simulation.alphaTarget(0.12).restart();
      });

    svg.on('click', () => onSelectNodeRef.current(null));

    const updateLinks = (selection: d3.Selection<SVGLineElement, SimLink, SVGGElement, unknown>) => {
      selection
        .attr('x1', (link) => {
          const threat = threatCrossEnds(link, nodeById);
          if (threat) return shortenEnds(threat.from, threat.to, 8).x1;
          return (link.source as SimNode).x ?? 0;
        })
        .attr('y1', (link) => {
          const threat = threatCrossEnds(link, nodeById);
          if (threat) return shortenEnds(threat.from, threat.to, 8).y1;
          return (link.source as SimNode).y ?? 0;
        })
        .attr('x2', (link) => {
          const threat = threatCrossEnds(link, nodeById);
          if (threat) return shortenEnds(threat.from, threat.to, 8).x2;
          return (link.target as SimNode).x ?? 0;
        })
        .attr('y2', (link) => {
          const threat = threatCrossEnds(link, nodeById);
          if (threat) return shortenEnds(threat.from, threat.to, 8).y2;
          return (link.target as SimNode).y ?? 0;
        });
    };

    simulation.on('tick', () => {
      updateLinks(linkSel as d3.Selection<SVGLineElement, SimLink, SVGGElement, unknown>);
      nodeGroup.attr('transform', (n) => `translate(${n.x ?? 0},${n.y ?? 0})`);
      if (!settled && simulation.alpha() < SETTLED_ALPHA) {
        settled = true;
        rememberPlaces(memory, nodes, true, held);
        fitIfPending();
        placeLabelSet();
      }
      positionLabels();
      hullJoin.select('path.c3-campaign-hull').attr('d', (d) => hullPathFor(d.members) || '');
      hullJoin.select('path.c3-campaign-hull-hit').attr('d', (d) => hullPathFor(d.members) || '');
    });

    return () => {
      simulation.stop();
      placeLabelsRef.current = null;
      rememberPlaces(memory, nodes, settled, held);
    };
  }, [graphData, dimensions, colorByDomain, layoutKey]);

  // Attenuate without rebuilding the force simulation (keeps node positions).
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    const root = d3.select(svgElement);
    const nodeById = new Map(graphData.nodes.map((n) => [n.id, n]));

    root.selectAll<SVGGElement, SimNode>('g.c3-sim-node').attr('opacity', (n) => {
      if (n.id === selectedNodeId) return 0.95;
      if (isNodeAttenuated(n, attenuatedInfraDomains, attenuatedNodeIds)) return DIM_NODE_OPACITY;
      return n.isDangling ? 0.65 : 0.95;
    });

    root.selectAll<SVGLineElement, SimLink>('line.c3-sim-link').attr('stroke-opacity', function (link) {
      const sourceId = linkEndpointId(link.source as SimNode | string);
      const targetId = linkEndpointId(link.target as SimNode | string);
      const source = sourceId
        ? nodeById.get(sourceId) || (typeof link.source === 'object' ? (link.source as SimNode) : undefined)
        : undefined;
      const target = targetId
        ? nodeById.get(targetId) || (typeof link.target === 'object' ? (link.target as SimNode) : undefined)
        : undefined;
      const dimmed =
        (source && isNodeAttenuated(source, attenuatedInfraDomains, attenuatedNodeIds)) ||
        (target && isNodeAttenuated(target, attenuatedInfraDomains, attenuatedNodeIds));
      // The lines of the selected node are drawn in indigo (the .is-selected-edge rule), as the selection ring is.
      const touchesSelection = Boolean(selectedNodeId && (sourceId === selectedNodeId || targetId === selectedNodeId));
      d3.select(this).classed('is-selected-edge', touchesSelection);
      if (dimmed) return DIM_LINK_OPACITY;
      if (touchesSelection) return 0.9;
      return linkOpacity({
        campaignMember: this.classList.contains('is-campaign-member'),
        reach: this.classList.contains('is-threat-cross'),
        crossLayer: link.crossLayer,
      });
    });
  }, [attenuatedInfraDomains, attenuatedNodeIds, selectedNodeId, graphData.nodes]);

  // Toggle infrastructure domain colors without restarting the force sim.
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    d3.select(svgElement)
      .selectAll<SVGPathElement, SimNode>('path.c3-sim-shape')
      .attr('fill', (node) => nodeFillColor(node, colorByDomain, domainColorsEnabled));
  }, [domainColorsEnabled, colorByDomain, graphData.nodes]);

  // Highlight scoped campaign hull without restarting the force sim.
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    paintCampaignHull(
      d3
        .select(svgElement)
        .selectAll<SVGPathElement, { key: string; members: SimNode[] }>('path.c3-campaign-hull'),
      activeCampaignId
    );
  }, [activeCampaignId, graphData]);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    d3.select(svgElement)
      .selectAll<SVGPathElement, SimNode>('path.c3-sim-shape')
      .attr('stroke', (node) => (node.id === selectedNodeId ? 'var(--t1)' : nodeStrokeColor(node)))
      .attr('stroke-width', (node) => (node.id === selectedNodeId ? 2.5 : nodeStrokeWidth(node)));
    placeLabelsRef.current?.();
  }, [selectedNodeId]);

  return (
    <div ref={containerRef} className={`h-full min-h-[360px] w-full ${className}`}>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="block"
        role="group"
        aria-label="Threat graph. Move between nodes with the arrow keys; Enter selects a node."
      />
    </div>
  );
};

export type { ForceGraphLink, ForceGraphNode };
export default ForceGraphDiagram;
